import React from "react";
import { ChevronRight } from "lucide-react";
import { Pill } from "@/components/vb";
import { calculatePercentageScore } from "@/variables";
import { signalNames } from "./variables";
import { SubscriptionBenefits } from "@/components/projects/components/SubscriptionBenefits";
import { SignalDrawer } from "@/components/projects/components/SignalDrawer";
import { signalStatus, statusTone, tierBand, plainExcerpt, signalFinding, severityRank } from "@/components/projects/components/signalCardUtils";

type Props = {
  signals: Record<string, any[]>;
  isLegacyReportFree: boolean; // is_paid=false AND signals.length > 10
  isFreeReport?: boolean; // is_paid=false AND signals.length <= 9
  isUserSubscribed?: boolean;
  canBeUpgraded?: boolean;
  reportId?: number;
  /** Formal signal names that have an actual Workbench fix (queue membership).
   *  Gates the drawer's "Start in Workbench" CTA. */
  workbenchFixNames: Set<string>;
  /** Navigate to the Workbench for this report (drawer footer CTA). */
  onStartWorkbench: () => void;
};

const ORDER = ["technical", "authority", "brand"] as const;

// Short taglines mirroring the prototype's tier subheads (presentational copy;
// the app's per-tier `signalDescription` is a full paragraph, too long here).
const TIER_TAGLINE: Record<string, string> = {
  technical: "How easily AI can find and read you",
  authority: "Whether AI trusts you as a credible source",
  brand: "How strongly your brand shows up",
};

function percentNumber(arr: any[]) {
  return parseFloat(calculatePercentageScore(arr).replace("%", ""));
}

const SCORE_LABEL: Record<string, string> = { pass: "Healthy", watch: "Issue", fail: "Critical", unknown: "—" };

type Selected = { signal: any; tierLabel: string; index: number; total: number };

export function SignalCards({
  signals,
  isLegacyReportFree,
  isFreeReport = false,
  isUserSubscribed,
  canBeUpgraded,
  reportId,
  workbenchFixNames,
  onStartWorkbench,
}: Props) {
  const entries: [string, any[]][] = ORDER.map((category) => [category, signals[category] ?? []]);

  const firstNonEmpty = entries.find(([, arr]) => arr.length > 0)?.[0] ?? ORDER[0];
  const [openTiers, setOpenTiers] = React.useState<Set<string>>(() => new Set([firstNonEmpty]));
  const [selected, setSelected] = React.useState<Selected | null>(null);

  const toggleTier = (key: string) => {
    setOpenTiers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div>
      <p className="vb-sig-intro">
        AI Trust Signals are the ingredients of your company's AI visibility and contribute to how both AI
        models and buyers think about your brand and products.
      </p>

      <div className="vb-tieracc">
        {entries.map(([key, tierSignals], tierIndex) => {
          const isOpen = openTiers.has(key);
          const pct = percentNumber(tierSignals);
          const band = tierBand(pct);

          const counts = { pass: 0, watch: 0, fail: 0 };
          tierSignals.forEach((s) => {
            const st = signalStatus(s.score, s.scoring_criteria);
            if (st === "pass") counts.pass += 1;
            else if (st === "watch") counts.watch += 1;
            else if (st === "fail") counts.fail += 1;
          });

          // Legacy-free reports gate every tier after the first.
          const gated = isLegacyReportFree && !isFreeReport && tierIndex > 0;

          return (
            <div className={`vb-tier${isOpen ? " vb-tier--open" : ""}`} key={key}>
              <button
                type="button"
                className="vb-tier__head"
                aria-expanded={isOpen}
                onClick={() => toggleTier(key)}
              >
                <span className="vb-tier__chev" aria-hidden="true">
                  <ChevronRight className="h-[13px] w-[13px]" strokeWidth={2.5} />
                </span>
                <span className="vb-tier__meta">
                  <span className="vb-tier__name">{signalNames[key as keyof typeof signalNames]}</span>
                  <span className="vb-tier__sub">
                    {TIER_TAGLINE[key]} · {tierSignals.length} signal{tierSignals.length === 1 ? "" : "s"}
                    {gated ? " · Locked" : ""}
                  </span>
                </span>
                {/* Locked tiers don't disclose their per-status breakdown — only
                    the tier score shows (matching the old tab UI's paywall). */}
                {gated ? (
                  <span />
                ) : (
                  <span className="vb-tier__counts">
                    <span className={`vb-tier__count${counts.fail === 0 ? " vb-tier__count--zero" : ""}`}>
                      <span className="vb-tier__dot vb-tier__dot--fail" />
                      {counts.fail}
                    </span>
                    <span className={`vb-tier__count${counts.watch === 0 ? " vb-tier__count--zero" : ""}`}>
                      <span className="vb-tier__dot vb-tier__dot--watch" />
                      {counts.watch}
                    </span>
                    <span className={`vb-tier__count${counts.pass === 0 ? " vb-tier__count--zero" : ""}`}>
                      <span className="vb-tier__dot vb-tier__dot--pass" />
                      {counts.pass}
                    </span>
                  </span>
                )}
                <span className={`vb-tier__score vb-tier__score--${band}`}>{pct}</span>
              </button>

              <div className="vb-tier__body">
                {gated ? (
                  <SubscriptionBenefits
                    isUserSubscribed={isUserSubscribed}
                    canBeUpgraded={canBeUpgraded}
                    isFreeReport={isFreeReport}
                    reportId={reportId}
                  />
                ) : (
                  <div className="vb-sigcard-grid">
                    {[...tierSignals]
                      .sort(
                        (a, b) =>
                          severityRank(signalStatus(a.score, a.scoring_criteria)) -
                          severityRank(signalStatus(b.score, b.scoring_criteria)),
                      )
                      .map((it, i) => {
                      const st = signalStatus(it.score, it.scoring_criteria);
                      const scoreText = it?.score ? String(it.score) : SCORE_LABEL[st];
                      // The personalized takeaway ("what does it mean for me"),
                      // falling back to the generic definition — see signalFinding.
                      // Only the takeaway needs markdown stripping; the drawer
                      // renders whichever one this picks, in full.
                      const finding = signalFinding(it);
                      const sub = finding.isFinding ? plainExcerpt(finding.text, 160) : finding.text;
                      return (
                        <button
                          type="button"
                          key={it?.id ?? i}
                          className={`vb-sigcard vb-sigcard--${st}`}
                          onClick={() =>
                            setSelected({
                              signal: it,
                              tierLabel: signalNames[key as keyof typeof signalNames],
                              index: i,
                              total: tierSignals.length,
                            })
                          }
                        >
                          <span className="vb-sigcard__top">
                            <span className="vb-sigcard__nm">{it?.name}</span>
                            <Pill tone={statusTone(st)} className="vb-sigcard__badge">
                              {scoreText}
                            </Pill>
                          </span>
                          {sub && <span className="vb-sigcard__sub">{sub}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Free reports (≤9 signals): the real signals show above; tease the locked
          premium set with the subscribe CTA. */}
      {isFreeReport && (
        <div className="mt-4">
          <SubscriptionBenefits
            isUserSubscribed={isUserSubscribed}
            canBeUpgraded={canBeUpgraded}
            isFreeReport={isFreeReport}
            reportId={reportId}
          />
        </div>
      )}

      <SignalDrawer
        signal={selected?.signal ?? null}
        tierLabel={selected?.tierLabel}
        index={selected?.index}
        total={selected?.total}
        workbenchFixNames={workbenchFixNames}
        onStartWorkbench={onStartWorkbench}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

export default SignalCards;
